import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Eye, Loader2 } from 'lucide-react';
import { useEmailTemplates } from '@/hooks/email/useEmailTemplates';
import TemplatePreview from '@/components/email/TemplatePreview';

interface TemplateSelectorProps {
  selectedTemplateId: string | null;
  onTemplateChange: (templateId: string) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onTemplateChange
}) => {
  const { templates, isLoading, getDefaultTemplate, getTemplateById } = useEmailTemplates();
  const [previewOpen, setPreviewOpen] = useState(false);

  // Auto-select default template if none selected
  React.useEffect(() => {
    if (!selectedTemplateId && templates.length > 0) {
      const defaultTemplate = getDefaultTemplate();
      if (defaultTemplate) {
        onTemplateChange(defaultTemplate.id);
      }
    }
  }, [templates, selectedTemplateId, getDefaultTemplate, onTemplateChange]);

  const selectedTemplate = selectedTemplateId ? getTemplateById(selectedTemplateId) : null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Laster maler...
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="email-template">E-postmal</Label>
      <div className="flex gap-2">
        <Select
          value={selectedTemplateId || ''}
          onValueChange={onTemplateChange}
        >
          <SelectTrigger id="email-template" className="flex-1">
            <SelectValue placeholder="Velg e-postmal..." />
          </SelectTrigger>
          <SelectContent>
            {templates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                <div className="flex flex-col">
                  <span className="font-medium">
                    {template.name}
                    {template.is_default && ' (standard)'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Fra: {template.sender_name} &lt;{template.sender_email}&gt;
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogTrigger asChild>
            <Button 
              type="button" 
              variant="outline" 
              size="icon"
              disabled={!selectedTemplate}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Forhåndsvisning: {selectedTemplate?.name}</DialogTitle>
            </DialogHeader>
            {selectedTemplate && (
              <TemplatePreview template={selectedTemplate} />
            )}
          </DialogContent>
        </Dialog>
      </div>
      {selectedTemplate && (
        <p className="text-xs text-muted-foreground">
          Avsender: <strong>{selectedTemplate.sender_name}</strong> ({selectedTemplate.sender_email})
          {selectedTemplate.description && ` — ${selectedTemplate.description}`}
        </p>
      )}
    </div>
  );
};

export default TemplateSelector;
